// MSMEOlympics.jsx

import React, { useEffect, useRef, useState } from 'react';

const CHALLENGES = ['Ploughing','Milking','Tractor Race'];
const ROUND_TIME_MS = 15000; // 15s per round default
const INITIAL_COINS = 100;

function loadLS(key, fallback){ try{ const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }catch(e){ return fallback; } }
function saveLS(key,val){ try{ localStorage.setItem(key, JSON.stringify(val)); }catch(e){} }

export default function MSMEOlympics(){
  const [coins, setCoins] = useState(() => loadLS('fo_coins', INITIAL_COINS));
  const [leaderboard, setLeaderboard] = useState(() => loadLS('fo_lb', []));
  const [active, setActive] = useState(null); // {name, startTs, timeMs, data}
  const [message, setMessage] = useState('Choose a challenge and show your farm skills!');
  const [roundScore, setRoundScore] = useState(0);
  const timerRef = useRef(null);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  useEffect(()=> saveLS('fo_coins', coins), [coins]);
  useEffect(()=> saveLS('fo_lb', leaderboard), [leaderboard]);

  function startRound(name){
    if(isRunning) { setMessage('Finish current round first.'); return; }
    setSelectedChallenge(name);
    setRoundScore(0);
    const now = Date.now();
    const data = setupChallengeData(name);
    setActive({ name, startTs: now, timeMs: ROUND_TIME_MS, data });
    setIsRunning(true);
    setMessage(`${name} started — go!`);
    timerRef.current = setTimeout(()=> finishRound(), ROUND_TIME_MS + 50);
  }

  function setupChallengeData(name){
    if(name === 'Ploughing'){
      // generate a bezier-like path of points (relative 0..1)
      const points = Array.from({length:6}).map((_,i)=> ({ x: 0.05 + i*0.15 + (Math.random()-0.5)*0.06, y: 0.1 + Math.random()*0.8 }));
      return { points, traced: [], accuracy: 0 };
    }
    if(name === 'Milking'){
      // require alternating taps between two pads, count hits
      return { leftCount:0, rightCount:0, last:'none', hits:0 };
    }
    if(name === 'Tractor Race'){
      // simple lane-based race: position, speed, obstacles
      return { pos:0, speed:0, lane:1, obstacles: randomObstacles(), hits:0 };
    }
    return {};
  }

  function randomObstacles(){
    return Array.from({length:6}).map(()=> ({ at: Math.random()*1, lane: Math.floor(Math.random()*3) }));
  }

  // Input handlers per challenge
  // Ploughing: track pointer moves, compute distance from path
  function ploughPointerMove(clientX, clientY){
    if(!active || active.name !== 'Ploughing') return;
    const el = document.getElementById('plough-area'); if(!el) return;
    const rect = el.getBoundingClientRect();
    const rx = (clientX - rect.left) / rect.width; const ry = (clientY - rect.top) / rect.height;
    // find nearest path point
    const pts = active.data.points;
    const nearestIdx = pts.reduce((best, p, i)=>{ const d = Math.hypot(p.x-rx, p.y-ry); return d < best.d ? {d, i} : best; }, {d:999, i:0}).i;
    const dist = Math.hypot(pts[nearestIdx].x - rx, pts[nearestIdx].y - ry);
    // update accuracy score (closer -> higher)
    const add = Math.max(0, Math.round((0.5 - Math.min(0.5, dist)) * 100));
    setActive(ac => ({ ...ac, data: {...ac.data, accuracy: (ac.data.accuracy||0) + add }}));
    setRoundScore(s => s + Math.max(0, Math.floor((60 - dist*100) / 10)));
  }

  // Milking: alternating fast taps
  function milkTap(side){ if(!active || active.name !== 'Milking') return; setActive(ac => { const d = {...ac.data}; if(side==='L'){ d.leftCount++; } else { d.rightCount++; } if(d.last !== side){ d.hits++; d.last = side; setRoundScore(s=> s + 10 + Math.floor(Math.random()*6)); } else { setRoundScore(s=> Math.max(0, s - 4)); } return {...ac, data:d}; }); }

  // Tractor Race: tap to boost, hold to maintain speed, avoid obstacles
  function tractorTap(){ if(!active || active.name !== 'Tractor Race') return; setActive(ac => { const d = {...ac.data}; d.speed = Math.min(8, (d.speed||0) + 1.2); d.pos = d.pos + d.speed*0.02; setRoundScore(s=> s + Math.round(d.speed*3)); return {...ac, data:d}; }); }

  // Game loop for tractor (obstacle collision, friction)
  useEffect(()=>{
    let t = null; if(isRunning && active && active.name === 'Tractor Race'){
      t = setInterval(()=>{
        setActive(ac => {
          if(!ac || ac.name !== 'Tractor Race') return ac;
          const d = {...ac.data}; d.speed = Math.max(0, (d.speed||0) - 0.08); d.pos = Math.min(1, d.pos + d.speed*0.015);
          // obstacle check
          d.obstacles.forEach(ob => { if(!ob._hit && Math.abs(ob.at - d.pos) < 0.035 && ob.lane === d.lane){ ob._hit = true; d.speed = Math.max(0, d.speed - 3); setRoundScore(s=> Math.max(0, s - 30)); } });
          return {...ac, data:d};
        });
      }, 80);
    }
    return ()=> { if(t) clearInterval(t); };
  }, [isRunning, active && active.name]);

  // finish round and compute medal
  function finishRound(){
    if(!active) return;
    const ac = active; let total = roundScore;
    // normalize metrics by challenge
    if(ac.name === 'Ploughing'){
      const acc = ac.data.accuracy || 0; total += Math.floor(acc/10);
    }
    if(ac.name === 'Milking'){ total += (ac.data.hits || 0) * 15; }
    if(ac.name === 'Tractor Race'){ total += Math.floor((ac.data.pos||0) * 400); }

    // medals
    let medal = null; if(total >= 600){ medal = 'gold'; setCoins(c=> c + 150); setMessage('Gold! Excellent farm skill.'); }
    else if(total >= 350){ medal = 'silver'; setCoins(c=> c + 90); setMessage('Silver — great job!'); }
    else if(total >= 150){ medal = 'bronze'; setCoins(c=> c + 35); setMessage('Bronze — good effort!'); }
    else { setMessage('Round complete — keep training to improve.'); }

    const entry = { ts: Date.now(), challenge: ac.name, score: total, medal };
    setLeaderboard(lb => { const nb = [entry, ...lb].slice(0,20); return nb; });

    // cleanup
    setActive(null); setIsRunning(false); setRoundScore(0); if(timerRef.current){ clearTimeout(timerRef.current); timerRef.current = null; }
  }

  function cancelRound(){ if(active){ setActive(null); setIsRunning(false); setMessage('Round cancelled'); if(timerRef.current){ clearTimeout(timerRef.current); timerRef.current=null; } } }

  // small UI helpers
  function formatDate(ts){ return new Date(ts).toLocaleString(); }
  function resetGame(){ if(!window.confirm('Reset MSME Olympics?')) return; localStorage.removeItem('fo_coins'); localStorage.removeItem('fo_lb'); window.location.reload(); }

  // UI: specific render helpers
  function renderPloughing(){ const pts = active.data.points || []; return (
    <div style={{display:'flex', flexDirection:'column', gap:8}}>
      <div id="plough-area" style={{height:220, background:'#efeae2', borderRadius:10, position:'relative', touchAction:'none'}} onPointerMove={(e)=> ploughPointerMove(e.clientX,e.clientY)} onPointerDown={(e)=> ploughPointerMove(e.clientX,e.clientY)}>
        {pts.map((p,i)=> (
          <div key={i} style={{position:'absolute', left:`${p.x*100}%`, top:`${p.y*100}%`, transform:'translate(-50%,-50%)', width:12, height:12, borderRadius:6, background:'#6ba84a', boxShadow:'0 4px 10px rgba(0,0,0,0.08)'}}></div>
        ))}
        <div style={{position:'absolute', right:8, bottom:8, fontSize:12, color:'#666'}}>Trace the furrows (move pointer over the path)</div>
      </div>
      <div className='panel'>Accuracy: <strong>{Math.round((active.data.accuracy||0)/10)}</strong></div>
    </div>
  ); }

  function renderMilking(){ const d = active.data; return (
    <div style={{display:'flex', gap:12, alignItems:'center', justifyContent:'center', flexWrap:'wrap'}}>
      <div style={{display:'flex', gap:8, alignItems:'center'}}>
        <button style={{padding:'14px 18px'}} onPointerDown={()=> milkTap('L')}>Left 🥛</button>
        <div style={{minWidth:120, textAlign:'center'}}>Hits: {d.hits || 0}</div>
        <button style={{padding:'14px 18px'}} onPointerDown={()=> milkTap('R')}>Right 🥛</button>
      </div>
      <div className='panel'>Score: <strong>{roundScore}</strong></div>
    </div>
  ); }

  function renderTractor(){ const d = active.data; return (
    <div style={{display:'flex', flexDirection:'column', gap:8}}>
      <div style={{height:160, background:'#f3f7ef', borderRadius:10, overflow:'hidden', position:'relative'}} onPointerDown={()=> tractorTap()}>
        {/* lanes */}
        {[0,1,2].map(l=> (
          <div key={l} style={{position:'absolute', left:0, right:0, top:`${l*(100/3)}%`, height:`${100/3}%`, borderTop:l>0? '1px solid rgba(0,0,0,0.06)':''}}></div>
        ))}
        {/* obstacles */}
        {d.obstacles.map((ob,i)=> (
          <div key={i} style={{position:'absolute', left:`${ob.at*100}%`, top:`${ob.lane*(100/3)+10}%`, transform:'translate(-50%,-50%)', width:24, height:24, borderRadius:6, background: ob._hit ? '#ddd' : '#a43', display:'flex', alignItems:'center', justifyContent:'center'}}>{ob._hit? '💨' : '⛔'}</div>
        ))}
        {/* tractor */}
        <div style={{position:'absolute', left:`${(d.pos||0)*100}%`, top:`${d.lane*(100/3)+20}%`, transform:'translate(-50%,-50%)', fontSize:28}}>🚜</div>
      </div>
      <div className='panel'>Speed: <strong>{(d.speed||0).toFixed(2)}</strong> • Lane: <strong>{d.lane+1}</strong></div>
      <div className="flex flex-row gap-2">
        <button onClick={()=> setActive(ac => ({...ac, data:{...ac.data, lane: Math.max(0, (ac.data.lane||1)-1)}}))}>Left</button>
        <button onClick={()=> setActive(ac => ({...ac, data:{...ac.data, lane: Math.min(2, (ac.data.lane||1)+1)}}))}>Right</button>
        <button onClick={()=> tractorTap()}>Tap to Boost</button>
      </div>
    </div>
  ); }

  return (
    <div style={{padding:16, maxWidth:1100, margin:'0 auto', fontFamily:'system-ui,Segoe UI,Roboto,Helvetica,Arial'}}>
      <style>{`
        .top{display:flex; gap:12px; align-items:center; justify-content:space-between; flex-wrap:wrap}
        .panel{background:#fff; padding:10px; border-radius:10px; box-shadow:0 6px 18px rgba(0,0,0,0.06)}
        .grid{display:grid; grid-template-columns:1fr 340px; gap:12px}
        .controls{display:flex; gap:8px; flex-wrap:wrap}
        @media(max-width:880px){ .grid{grid-template-columns:1fr} }
      `}</style>

      <div className='top'>
        <div style={{display:'flex', gap:12, alignItems:'center'}}>
          <h2 style={{margin:0}}>🏅 MSME Olympics</h2>
          <div className='panel'>Coins: <strong>₹{coins}</strong></div>
        </div>

        <div className='flex flex-row gap-2'>
          <button onClick={()=> startRound('Ploughing')}>Ploughing</button>
          <button onClick={()=> startRound('Milking')}>Milking</button>
          <button onClick={()=> startRound('Tractor Race')}>Tractor Race</button>
          <button onClick={cancelRound}>Cancel</button>
          <button onClick={resetGame}>Reset</button>
        </div>
      </div>

      <div className='grid' style={{marginTop:12}}>
        <div>
          <div className='panel'>
            <h3 className='text-blue-700'>Challenge Area</h3>
            {active ? (
              <div>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <div><strong>{active.name}</strong></div>
                  <div className='panel'>Time left: <strong>{Math.max(0, Math.ceil(((active.startTs + active.timeMs) - Date.now())/1000))}s</strong></div>
                </div>

                <div style={{marginTop:10}}>
                  {active.name === 'Ploughing' && renderPloughing()}
                  {active.name === 'Milking' && renderMilking()}
                  {active.name === 'Tractor Race' && renderTractor()}
                </div>

                <div style={{display:'flex', gap:8, marginTop:12}}>
                  <button onClick={finishRound}>Finish</button>
                  <button onClick={cancelRound}>Cancel</button>
                </div>

              </div>
            ) : (
              <div className='panel'>No active challenge — pick one and show your skills!</div>
            )}
          </div>

          <div className='panel' style={{marginTop:12}}>
            <h4 style={{marginTop:0}}>Round Score: <strong>{roundScore}</strong></h4>
            <div>{message}</div>
          </div>

        </div>

        <aside>
          <div className='panel'>
            <h4 style={{marginTop:0}}>Local Leaderboard</h4>
            {leaderboard.length === 0 ? <div style={{color:'#666'}}>No runs yet</div> : (
              <ol style={{paddingLeft:16}}>
                {leaderboard.map((e,i)=> (
                  <li key={i} style={{marginBottom:6}}>{formatDate(e.ts)} — {e.challenge} — {e.score}{e.medal? ` • ${e.medal.toUpperCase()}` : ''}</li>
                ))}
              </ol>
            )}
          </div>

          <div className='panel' style={{marginTop:12}}>
            <h4 style={{marginTop:0}}>Tips</h4>
            <ul style={{paddingLeft:18}}>
              <li>Ploughing: trace the path closely for high accuracy.</li>
              <li>Milking: alternate quickly between left & right for combos.</li>
              <li>Tractor Race: tap for bursts, steer to avoid obstacles.</li>
            </ul>
          </div>
        </aside>
      </div>

      <footer className='bg-gray-500 mt-12'>MSME Olympics — quick, addictive mini-challenges for farmers. Want SFX, animations, or online leaderboards next?</footer>
    </div>
  );
}
